import { Badge, Button, cn } from '@aibi/ui';
import { CircleDot, Monitor, RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import { useServices } from '../../../app/services';
import {
  useServerHealth,
  type ServerHealthState,
} from '../../../features/system-status/use-server-health';
import { getDesktopBridge } from '../../../platform/desktop';
import type { QueryDataMode } from '../../services/query-result-source';
import { useUiState } from '../../state/ui-state';

const STATUS_LABEL: Readonly<Record<ServerHealthState['status'], string>> = {
  checking: 'Comprobando...',
  online: 'En linea',
  offline: 'Sin conexion',
};

/** Barra inferior: estado real del servidor, plataforma de ejecucion y origen de datos. */
export function StatusBar() {
  const { healthApi, queryResultSource } = useServices();
  const { activeModelId } = useUiState();
  const { state, refresh } = useServerHealth(healthApi);
  const desktop = useMemo(() => getDesktopBridge(), []);
  const dataMode: QueryDataMode | null =
    activeModelId !== null && queryResultSource.modeFor
      ? queryResultSource.modeFor(activeModelId)
      : queryResultSource.kind === 'preview'
        ? 'preview'
        : null;

  return (
    <footer
      aria-label="Barra de estado"
      className="flex h-7 shrink-0 items-center gap-4 border-t border-border bg-surface px-3 text-[11px] text-muted-foreground"
    >
      <span className="inline-flex items-center gap-1.5">
        <CircleDot
          aria-hidden
          className={cn(
            'size-3',
            state.status === 'online' && 'text-success',
            state.status === 'offline' && 'text-destructive',
          )}
        />
        <span className="sr-only">Servidor:</span>
        <span role="status">{STATUS_LABEL[state.status]}</span>
      </span>
      {state.status === 'online' && (
        <span className="inline-flex items-center gap-1">
          <span>Version</span>
          <span className="font-mono">{state.health.version}</span>
        </span>
      )}
      {state.status === 'offline' && (
        <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[11px]" onClick={refresh}>
          <RefreshCw /> Reintentar
        </Button>
      )}
      <span className="inline-flex items-center gap-1">
        <Monitor aria-hidden className="size-3" />
        <span>{desktop ? `Escritorio (Electron ${desktop.versions.electron})` : 'Navegador'}</span>
      </span>
      <span className="ml-auto">
        {dataMode === 'preview' && (
          <Badge
            variant="warning"
            title="Los resultados son sinteticos y deterministas; no provienen de una fuente real."
          >
            Datos de demostracion
          </Badge>
        )}
        {dataMode === 'live' && (
          <Badge
            variant="success"
            title="Los resultados se consultan en vivo en las fuentes configuradas en el servidor."
          >
            Datos reales
          </Badge>
        )}
      </span>
    </footer>
  );
}
